import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Alert,
  Text,
  LoadingOverlay,
  Box,
  Checkbox
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconAlertCircle, IconUser, IconLock } from '@tabler/icons-react'
import { loginUser, clearError } from '../../store/slices/authSlice'

function LoginForm({ onSuccess, onSwitchToRegister }) {
  const dispatch = useDispatch()
  const { loading, error } = useSelector((state) => state.auth)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const form = useForm({
    initialValues: {
      username: '',
      password: '',
      rememberMe: false
    },
    validate: {
      username: (value) => {
        if (!value) return 'Username is required'
        if (value.length < 3) return 'Username must be at least 3 characters'
        return null
      },
      password: (value) => {
        if (!value) return 'Password is required'
        if (value.length < 3) return 'Password must be at least 3 characters'
        return null
      }
    }
  })

  const handleSubmit = async (values) => {
    setSubmitAttempted(true)
    
    if (error) {
      dispatch(clearError())
    }

    try {
      // Only send username and password to backend, not rememberMe
      const credentials = {
        username: values.username,
        password: values.password
      }
      
      const result = await dispatch(loginUser(credentials))
      if (loginUser.fulfilled.match(result)) {
        form.reset()
        setSubmitAttempted(false)
        if (onSuccess) {
          onSuccess(result.payload)
        }
      }
    } catch (err) {
      console.error('Login failed:', err)
    }
  }

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} />
      
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="lg" fw={600} ta="center" c="blue">
            Welcome Back
          </Text>

          {error && submitAttempted && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title="Login Failed"
              withCloseButton
              onClose={() => dispatch(clearError())}
            >
              {error}
            </Alert>
          )}

          <TextInput
            label="Username"
            placeholder="Enter your username"
            leftSection={<IconUser size={16} />}
            required
            {...form.getInputProps('username')}
            error={form.errors.username}
          />

          <PasswordInput
            label="Password"
            placeholder="Enter your password"
            leftSection={<IconLock size={16} />}
            required
            {...form.getInputProps('password')}
            error={form.errors.password}
          />

          <Checkbox
            label="Remember me"
            {...form.getInputProps('rememberMe', { type: 'checkbox' })}
          />

          <Button
            type="submit"
            fullWidth
            loading={loading}
            disabled={loading}
            size="md"
          >
            Sign In
          </Button>

          <Text size="sm" ta="center" c="dimmed">
            Don't have an account?{' '}
            <Text
              component="button"
              type="button"
              variant="link"
              c="blue"
              fw={500}
              onClick={onSwitchToRegister}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Create one here
            </Text>
          </Text>
        </Stack>
      </form>
    </Box>
  )
}

export default LoginForm